import { useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import type {
  ConversationListEntry,
  ConversationListResponse,
} from '../modules/conversations/types.js'

export interface UseRecentConversationsReturn {
  conversations: ConversationListEntry[]
  loading: boolean
  error: string | null
}

/**
 * §0.I / Task 20 — fetches the most-recent conversations for a contact via
 * GET /conversations?contactId={contactId}&limit={limit} (UCP-0c filter).
 * Front-loaded as part of Task 13 since §0.D's "Recent activity" section
 * depends on it.
 */
export function useRecentConversations(
  id: string | null,
  limit = 5,
  kind: 'contact' | 'client' = 'contact',
  refreshKey = 0,
): UseRecentConversationsReturn {
  const [conversations, setConversations] = useState<ConversationListEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (id === null) {
      setConversations([])
      setError(null)
      setLoading(false)
      return
    }
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    params.set(kind === 'client' ? 'clientId' : 'contactId', id)
    params.set('limit', String(limit))
    api
      .get<ConversationListResponse>(`/conversations?${params.toString()}`)
      .then((resp) => {
        if (ctrl.signal.aborted) return
        setConversations(resp.items)
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return
        setError(`Failed to load recent conversations: ${(err as Error).message}`)
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false)
      })
    return () => ctrl.abort()
  }, [id, limit, kind, refreshKey])

  return { conversations, loading, error }
}
